import { VStack } from "@chakra-ui/react";
import React, { useCallback, useMemo } from "react";
import OccupancyBarChart, { OccupancyBarData } from "./charts/OccupancyBarChart";
import { useResizeObserver } from "@tether-web-portal/hooks";
import OccupancyBaseTile, {
  OccupancyBaseTileChildProps,
  Unavailable,
} from "@tether-web-portal/components/occupancy/tiles/OccupancyBaseTile";
import { OccupancyAggregate } from "@tether-web-portal/web-workers/occupancy";
import { UnconfiguredRoomsWarning } from "./UnconfiguredRoomsWarning";

interface OccupancyQuickGlanceTileProps extends OccupancyBaseTileChildProps {
  data?: OccupancyAggregate[];
  /**
   * Rooms contributing to building totals that have no configured capacity.
   * When non-empty the tile renders the same orange-triangle warning as the
   * Total Occupancy tile.
   */
  unconfiguredRooms?: { id: string; name: string }[];
}

// The aggregate id is formatted as "DD/MM/YYYY" (DATE_FORMATS.DAY_MONTH_YEAR_SLASHED).
// Shorten to "D MMM" for the x-axis tick so 6-7 dates fit across the tile width.
const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const shortenDateLabel = (slashed: string): string => {
  const [dayStr, monthStr] = slashed.split("/");
  const day = parseInt(dayStr, 10);
  const monthIdx = parseInt(monthStr, 10) - 1;
  if (Number.isNaN(day) || monthIdx < 0 || monthIdx > 11) return slashed;
  return `${day} ${MONTHS_SHORT[monthIdx]}`;
};

const OccupancyQuickGlanceTile = ({
  data = [],
  loading,
  onAction,
  unconfiguredRooms,
  ...rest
}: OccupancyQuickGlanceTileProps) => {
  const { width, setRef } = useResizeObserver();

  const xLabelFormatter = useCallback((label: string) => shortenDateLabel(label), []);

  const { chartItems, unavailable } = useMemo(() => {
    if (loading) {
      return {
        chartItems: [],
        isEmpty: true,
      };
    }

    const sanitisedData: OccupancyBarData[] =
      data?.map((item) => {
        return {
          label: item.id,
          value: item.usage,
          tooltip: `${item.id}, Avg occupancy: ${item.usage.toFixed(0)}%`,
        };
      }) || [];

    const isEmpty = sanitisedData.filter((item) => item.value > 0).length === 0;

    return {
      unavailable: {
        icon: "glance",
        enabled: !loading && isEmpty,
        title: "No occupancy data available",
        subtitle: "Occupancy information will appear here once data is collected.",
      } as Unavailable,
      chartItems: sanitisedData,
    };
  }, [data, loading]);

  const titleAdornment =
    unconfiguredRooms && unconfiguredRooms.length > 0 ? (
      <UnconfiguredRoomsWarning rooms={unconfiguredRooms} />
    ) : undefined;

  return (
    <OccupancyBaseTile
      ref={setRef}
      title="Quick Glance"
      titleAdornment={titleAdornment}
      loading={loading}
      unavailable={unavailable}
      onAction={unavailable?.enabled ? undefined : onAction}
      {...rest}
    >
      <VStack alignItems="flex-start" spacing={2}>
        <OccupancyBarChart
          width={width}
          height={150}
          data={chartItems}
          xLabelEvery={5}
          xLabelFormatter={xLabelFormatter}
        />
      </VStack>
    </OccupancyBaseTile>
  );
};

export default React.memo(OccupancyQuickGlanceTile);
